import Media from 'react-media';
import { Icon } from '@eti/components';
import { useDateFormat, useSearch } from '@eti/providers';
import { css, etiColors } from '@eti/styles';
import { date as dateUtil } from '@eti/utils';
import { ONE_WAY } from '../../../constants/tripTypesConstants';

const titleStyles = css`
  margin: auto 0;

  @media (min-width: 60.625em) {
    align-items: center;
    display: flex;
  }
`;

const titleTextStyles = css`
  color: ${etiColors.black};
  display: flex;
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;

  @media (min-width: 40em) {
    font-size: 0.875rem;
  }

  @media (min-width: 60.625em) {
    font-size: 1rem;
    margin-bottom: 0;
  }
`;

const separatorStyles = (large) => css`
  display: inline-block;
  padding: 0 ${large ? '0.35rem' : '0.2rem'};
`;

const detailsStyles = css`
  align-items: center;
  color: ${etiColors.black};
  display: flex;

  @media (min-width: 60.625em) {
    margin-left: 1.25rem;

    [dir='rtl'] & {
      margin-left: 0;
      margin-right: 1.25rem;
    }
  }
`;

const detailsItemStyles = css`
  align-items: center;
  display: flex;
  font-size: 0.625rem;

  & + & {
    margin-left: 1rem;
  }

  @media (min-width: 40em) {
    font-size: 0.75rem;
  }

  @media (min-width: 60.625em) {
    font-size: 0.875rem;
  }

  [dir='rtl'] & + & {
    margin-left: 0;
    margin-right: 1rem;
  }
`;

const detailsIconStyles = css`
  fill: ${etiColors.grayScale[8]};
  margin-right: 0.5rem;

  [dir='rtl'] & {
    margin-left: 0.5rem;
    margin-right: 0;
  }
`;

const TripDetailsTitle = () => {
  const { currentSearchData } = useSearch();
  const { day, shortWeekday } = useDateFormat();
  const { date, destination, origin, travelers, tripType } = currentSearchData;
  const numberOfTravelers = travelers.adults + travelers.childAges.length;

  return (
    <div className={titleStyles}>
      <div className={titleTextStyles} data-testid="tripDetails-title-TitleText">
        <span>{origin.city}</span>
        <span className={separatorStyles(true)}>–</span>
        <span>{destination.city}</span>
      </div>

      <div className={detailsStyles}>
        <div className={detailsItemStyles}>
          <Icon className={detailsIconStyles} id="calendar" size="1rem" />
          <Media query="(min-width: 48em)">
            {(matches) =>
              matches ? (
                <div data-testid="tripDetails-title-date-desktop">
                  <time dateTime={date.departure}>
                    {dateUtil.format(date.departure, shortWeekday)}
                  </time>
                  {tripType !== ONE_WAY && (
                    <>
                      <span
                        className={separatorStyles()}
                        data-testid="tripDetails-title-separator-desktop"
                      >
                        –
                      </span>
                      <time dateTime={date.return}>
                        {dateUtil.format(date.return, shortWeekday)}
                      </time>
                    </>
                  )}
                </div>
              ) : (
                <div data-testid="tripDetails-title-date-mobile">
                  <time dateTime={date.departure}>{dateUtil.format(date.departure, day)}</time>
                  {tripType !== ONE_WAY && (
                    <>
                      <span
                        className={separatorStyles()}
                        data-testid="tripDetails-title-separator-mobile"
                      >
                        –
                      </span>
                      <time dateTime={date.return}>{dateUtil.format(date.return, day)}</time>
                    </>
                  )}
                </div>
              )
            }
          </Media>
        </div>
        <div className={detailsItemStyles}>
          <Icon className={detailsIconStyles} id="adult-traveler" size="1rem" />
          <Media query="(min-width: 48em)">
            {(matches) =>
              matches ? (
                <div data-testid="tripDetails-title-travelers-desktop">{travelers.summary}</div>
              ) : (
                <div data-testid="tripDetails-title-travelers-mobile">{numberOfTravelers}</div>
              )
            }
          </Media>
        </div>
      </div>
    </div>
  );
};

export default TripDetailsTitle;
